import { medicalJournalsService } from './medicalJournalsService';
import { medicsService } from './medicsService';
import { cryptoProService } from './cryptoProService';
import { TokenType } from '../models/enums/tokenType';
import { SigningStep, SigningStepUtil } from '../models/enums/signingStep';
import { SigningContext } from '../models/enums/signingContext';
import { MedicalJournalSigningData } from '../models/medicalJournals/bulkSignMedicalJournalsWithPhysicalTokenDto';
import { ValidationError, WorkplaceError, NetworkError, NETWORK_ERROR_MESSAGE, isNetworkError } from './medicalCheckupSigningService';

export interface SigningProgress {
    step: SigningStep;
    message: string;
}

export interface SigningOptions {
    onProgress?: (progress: SigningProgress) => void;
}

class MedicalJournalSigningService {
    /**
     * Уведомление о прогрессе подписания
     */
    private notifyProgress(
        onProgress: ((progress: SigningProgress) => void) | undefined,
        step: SigningStep,
        context?: SigningContext
    ): void {
        onProgress?.({
            step,
            message: SigningStepUtil.getMessage(step, context)
        });
    }

    /**
     * Подписание журналов через облачный сертификат (Signer Service)
     */
    async signWithCloudToken(
        journalIds: number[],
        onProgress?: (progress: SigningProgress) => void
    ): Promise<void> {
        try {
            this.notifyProgress(onProgress, SigningStep.Sending, SigningContext.SignerService);
            await medicalJournalsService.bulkSignWithCloudToken(journalIds);
            this.notifyProgress(onProgress, SigningStep.Completed);
        } catch (error: any) {
            if (error.response) {
                const errorMessage = error.response?.data?.message ?? error.message ?? 'Ошибка валидации';
                throw new ValidationError(errorMessage);
            }
            if (isNetworkError(error)) {
                throw new NetworkError(NETWORK_ERROR_MESSAGE);
            }
            throw error;
        }
    }

    /**
     * Подписание журналов через физический токен
     */
    async signWithPhysicalToken(
        journalIds: number[],
        thumbprint: string,
        onProgress?: (progress: SigningProgress) => void
    ): Promise<void> {
        try {
            // 1. Проверяем готовность рабочего места (плагин, CSP, наличие сертификатов на USB)
            const workplaceStatus = await cryptoProService.checkWorkplaceReadiness();
            if (!workplaceStatus.isReady) {
                throw new Error(workplaceStatus.errors || "Рабочее место не готово к подписанию журналов");
            }

            const signingData: MedicalJournalSigningData[] = [];

            // 2. Проверяем, что сертификат с переданным thumbprint реально доступен на USB-ключе.
            await cryptoProService.getCertificateInfo(thumbprint);

            // 3. Получаем хеши для всех журналов одним запросом
            this.notifyProgress(
                onProgress, 
                SigningStep.GettingHash,
                undefined
            );

            const hashResults = await medicalJournalsService.createHashes(journalIds);

            // Для каждого журнала: подписываем полученный хеш
            for (let i = 0; i < journalIds.length; i++) {
                const journalId = journalIds[i];
                const hashResult = hashResults[journalId];
                
                if (!hashResult) {
                    throw new Error(`Не удалось получить хеш для журнала с ID ${journalId}`);
                }

                const { json, hash } = hashResult;

                this.notifyProgress(onProgress, SigningStep.Signing);
                const signature = await cryptoProService.sign(
                    thumbprint,
                    btoa(json),
                    {
                        detached: true,
                        addUserCertificate: true,
                    }
                );

                signingData.push({
                    journalId,
                    signature,
                    hash,
                });
            }

            // Отправляем все подписи на бэкенд
            this.notifyProgress(onProgress, SigningStep.Sending);
            await medicalJournalsService.bulkSignWithPhysicalToken({
                journals: signingData,
            });

            this.notifyProgress(onProgress, SigningStep.Completed);
        } catch (error: any) {
            if (error.response) {
                const errorMessage = error.response?.data?.message ?? error.message ?? 'Ошибка валидации';
                throw new ValidationError(errorMessage);
            }
            if (isNetworkError(error)) {
                throw new NetworkError(NETWORK_ERROR_MESSAGE);
            }
            const normalizedError = cryptoProService.normalizeError(error, 'журналов');
            throw new WorkplaceError(normalizedError);
        }
    }

    /**
     * Массовое подписание журналов с автоматическим определением типа подписания
     */
    async signMedicalJournals(
        journalIds: number[],
        options: SigningOptions = {}
    ): Promise<void> {
        const { onProgress } = options;

        try {
            // Проверяем тип сертификата медика (только сертификаты по умолчанию)
            this.notifyProgress(onProgress, SigningStep.CheckingCertificate);

            const defaultCert = await medicsService.getMedicCertificate();

            if (!defaultCert) {
                throw new Error("Не найден действующий сертификат по умолчанию");
            }

            // Если есть сертификат Cloud, подписываем через Signer Service
            if (defaultCert.tokenType === TokenType.Cloud) {
                await this.signWithCloudToken(journalIds, onProgress);
                return;
            }

            // Если это Physical токен, подписываем через физический токен
            if (defaultCert.tokenType === TokenType.Physical) {
                await this.signWithPhysicalToken(
                    journalIds,
                    defaultCert.thumbprint,
                    onProgress
                );
                return;
            }

            throw new Error("Неизвестный тип токена");
        } catch (error: any) {
            if (error instanceof ValidationError || error instanceof WorkplaceError || error instanceof NetworkError) {
                throw error;
            }
            const normalizedError = cryptoProService.normalizeError(error, 'журналов');
            throw new WorkplaceError(normalizedError);
        }
    }
}

export const medicalJournalSigningService = new MedicalJournalSigningService();

